import {includes} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'

import {
    EntryType,
    FamilyPayMemberPaymentTypeValues,
    FamilyPayStatement,
    FamilyUser,
    FamilyUserType,
    OrderEntry,
    PayMethod,
    PayMethodType,
} from '@web/core/constants'

interface GetFamilyPayStatementProps {
    memberPaymentInfo: Maybe<{
        isFamilyPayAllowed: boolean
        type: FamilyPayMemberPaymentTypeValues
        isPreApprovalPaymentAllowed: boolean
    }>
    role: Maybe<FamilyUserType>
    entry: Maybe<EntryType>
    availablePayMethodTypes: PayMethodType[]
    naverPlusMembershipSubscribed: boolean
    familyPaySubscribed: boolean
    status: 'IDLE' | 'PENDING' | 'INVALID' | 'STABLE'
    invalidReason?: 'FETCH_FAIL'
}

export const getFamilyPayStatement = ({
    memberPaymentInfo,
    role,
    entry,
    availablePayMethodTypes,
    naverPlusMembershipSubscribed,
    familyPaySubscribed,
    status,
    invalidReason,
}: GetFamilyPayStatementProps) => {
    const isFamilyPayAllowed = memberPaymentInfo?.isFamilyPayAllowed
    const familyPayPaymentType = memberPaymentInfo?.type
    const isPreApprovalPaymentAllowed = memberPaymentInfo?.isPreApprovalPaymentAllowed
    const familyPayViewable = includes(entry, [
        OrderEntry.CHECKOUT,
        OrderEntry.EASY_BOOKING,
        OrderEntry.LOCAL_PAY,
        OrderEntry.SHOPN,
        OrderEntry.BRANCH,
        OrderEntry.GIFTING,
    ])
    const isMember = eq(role, FamilyUser.MEMBER)
    const isPayer = eq(role, FamilyUser.PAYER)

    const isFamilyPayAvailable = includes(PayMethod.FAMILY_PAY, availablePayMethodTypes)

    /** 패밀리결제 사용 가능한 주문서 */
    if (isFamilyPayAvailable) {
        /**
         * 네이버 플러스 맴버쉽 미가입시 미노출
         * TODO: 추후 네이버 플러스 맴버쉽 미가입자도 패밀리페이를 가입할 수 있게 되면 이 조건은 삭제된다.
         */
        if (!naverPlusMembershipSubscribed) {
            return FamilyPayStatement.HIDE
            /** 네이버플러스 맴버쉽 가입자인데 패밀리결제 미가입시 가입 대상이므로 가입 유도 */
        } else if (status !== 'STABLE') {
            return invalidReason === 'FETCH_FAIL' ? FamilyPayStatement.API_FAIL : FamilyPayStatement.HIDE
        } else if (!familyPaySubscribed) {
            return FamilyPayStatement.SIGN_UP_PROMPT
            /** 패밀리결제에 가입되어있는 대표는 미노출한다. */
        } else if (isPayer) {
            return FamilyPayStatement.HIDE
            /** 맴버인데 패밀리결제 허용되지 않았으면 미노출 */
        } else if (!isFamilyPayAllowed) {
            return FamilyPayStatement.HIDE
        } else {
            /** 맴버의 결제 타입에 따른 분기 */
            switch (familyPayPaymentType) {
                /** 사전승인결제 */
                case 'PRE_APPROVAL_PAYMENT':
                    return FamilyPayStatement.PRE_APPROVAL_PAYMENT
                /** 승인결제 */
                case 'APPROVAL_PAYMENT':
                    /** 사전승인결제가 허용 되었음에도 승인결제를 쓰게 되는 이유는 단건 한도 / 월 한도 금액 초과 */
                    return isPreApprovalPaymentAllowed
                        ? FamilyPayStatement.APPROVAL_PAYMENT_BY_PRE_APPROVAL_PAYMENT_DISABLED
                        : FamilyPayStatement.APPROVAL_PAYMENT
                /**
                 * 불가능
                 * 사전승인결제가 가능하지만 단건 한도 / 월 한도 금액 초과로 사용 불가
                 */
                case 'UNAVAILABLE':
                    return FamilyPayStatement.DISABLE
                /** 그외 모두 미노출 */
                default:
                    return FamilyPayStatement.HIDE
            }
        }
    } else {
        /**
         * 비활성화 노출
         * - 패밀리결제 사용은 불가하지만 추후 확장될 예정이어서 노출은 해줘야하는 주문서타입
         * - 패밀리결제가 허용된 맴버
         */
        if (familyPayViewable && isMember && isFamilyPayAllowed) {
            return FamilyPayStatement.DISABLE
        }
        /** 그 외 모두 미노출 */
        return FamilyPayStatement.HIDE
    }
}
